import { RefObject, useCallback, useEffect, useState } from "react";

interface AvailableSpaceOptions {
  /** Minimum height for the editor content */
  minHeight?: number;
  /** Bottom margin to leave space for buttons/alerts */
  bottomMargin?: number;
  /** External dependencies that should trigger recalculation when they change */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recalculateDependencies?: any[];
}

/** Tolerance in pixels for detecting alerts positioned near the editor */
const ALERT_POSITION_TOLERANCE = 50;

/**
 * Custom hook to calculate editor height based on available viewport space.
 * Recalculates on window resize and when elements around the editor change.
 */
export const useAvailableSpaceHeight = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorRef: RefObject<any>,
  options: AvailableSpaceOptions = {}
) => {
  const {
    minHeight = 200,
    bottomMargin = 100,
    recalculateDependencies = []
  } = options;

  const [editorHeight, setEditorHeight] = useState<number>(minHeight);

  const calculateHeight = useCallback(() => {
    try {
      const editorElement =
        editorRef.current?.querySelector?.(".k-editor-content") ||
        document.querySelector(".k-editor-content");

      if (!editorElement) {
        setEditorHeight(minHeight);
        return;
      }

      // Get the editor's position relative to viewport
      const editorRect = editorElement.getBoundingClientRect();
      const editorTop = editorRect.top;

      // Find the scrollable parent container (if any)
      let scrollableParent: HTMLElement | null = null;
      let parentElement = editorElement.parentElement;

      while (parentElement && parentElement !== document.body) {
        const overflow = window.getComputedStyle(parentElement).overflow;
        const overflowY = window.getComputedStyle(parentElement).overflowY;

        if (
          overflow === "auto" ||
          overflow === "scroll" ||
          overflowY === "auto" ||
          overflowY === "scroll"
        ) {
          scrollableParent = parentElement;
          break;
        }
        parentElement = parentElement.parentElement;
      }

      // Calculate height of alert/warning elements below the editor
      let alertHeight = 0;

      // Look for alerts in a broader scope - check scrollable parent or body
      const searchContainer = scrollableParent || document.body;

      // Check for alert/warning elements (including MuiAlert)
      // Use specific selectors to avoid false positives (e.g., btn-alert-primary)
      const alerts = searchContainer.querySelectorAll(
        '[role="alert"], .MuiAlert-root, .k-notification, .alert, [class^="alert-"], [class*=" alert-"], [class^="warning-"], [class*=" warning-"]'
      );

      alerts.forEach((alert: Element) => {
        const alertElement = alert as HTMLElement;
        const alertRect = alertElement.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(alertElement);

        // Only count visible alerts that appear below the editor
        if (
          computedStyle.display !== "none" &&
          computedStyle.visibility !== "hidden" &&
          alertRect.height > 0 &&
          alertRect.top > editorRect.bottom - ALERT_POSITION_TOLERANCE
        ) {
          alertHeight += alertRect.height;
        }
      });

      // Calculate available space
      const containerBottom = scrollableParent
        ? scrollableParent.getBoundingClientRect().bottom
        : window.innerHeight;

      const availableSpace =
        containerBottom - editorTop - bottomMargin - alertHeight;

      // Use the larger of available space or minimum height
      const calculatedHeight = Math.max(minHeight, availableSpace);

      setEditorHeight(calculatedHeight);
    } catch (error) {
      console.warn("Error calculating editor height:", error);
      setEditorHeight(minHeight);
    }
  }, [editorRef, minHeight, bottomMargin]);

  useEffect(() => {
    // Initial calculation
    calculateHeight();

    window.addEventListener("resize", calculateHeight);

    // Find the editor's container to scope observers
    const editorElement =
      editorRef.current?.querySelector?.(".k-editor-content") ||
      document.querySelector(".k-editor-content");
    const observeContainer =
      editorElement?.closest(".k-editor")?.parentElement || document.body;

    // Use ResizeObserver to detect changes in the container layout
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let rafId: number | null = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(calculateHeight);
      resizeObserver.observe(observeContainer);
    }

    // Use MutationObserver to detect when alerts or other elements are added/removed
    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(() => {
        // Debounce: cancel pending calculation and schedule new one
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          calculateHeight();
          rafId = null;
        });
      });

      // Observe only the editor's container, not the entire document
      mutationObserver.observe(observeContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"]
      });
    }

    // Cleanup
    return () => {
      window.removeEventListener("resize", calculateHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [calculateHeight, editorRef]);

  // Recalculate when editor ref changes
  useEffect(() => {
    if (editorRef.current) {
      requestAnimationFrame(calculateHeight);
    }
  }, [editorRef, calculateHeight]);

  // Recalculate when external dependencies change (e.g., CC/BCC fields shown/hidden)
  useEffect(() => {
    // Use double requestAnimationFrame to ensure DOM has fully updated
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        calculateHeight();
      });
    });

    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, recalculateDependencies);

  return {
    editorHeight,
    recalculateHeight: calculateHeight
  };
};
